<script setup lang="ts">
import { AppText } from '@shared/ui/typography'
import { NativeSelect } from '@shared/ui/fields'
import { BtnRadio } from '@shared/ui/buttons'

import { DisciplinePaymentOption } from '../model/enums'
import { useDisciplinePaymentOptions } from '../model/discipline-payment-options.hook'
import DisciplineOrderExpires from './DisciplineOrderExpires.vue'

defineOptions({
  name: 'DisciplinePaymentOptions',
})

const {
  refIsOneTime,
  refIsInstallment,
  refCanChooseOneTime,
  refCanChooseInstallment,
  refIsBusy,
  refIsAutoRenew,
  refIsFourWeeks,
  refPackageOptions,
  refInstallmentOptions,
  refDiscountOptions,
  refExpiresAt,
  refCustomerNow,
  refTimezone,
  setPaymentOption,
  setAutoRenew,
  setPackage,
  setInstallment,
  setDiscount,
} = useDisciplinePaymentOptions()
</script>

<template>
  <div class="discipline-payment-options">
    <div class="discipline-payment-options__choices">
      <div class="discipline-payment-options__choice">
        <span class="discipline-payment-options__label required">
          Payment option
        </span>
        <div
          class="discipline-payment-options__radios"
          role="radiogroup"
          aria-label="Payment option"
        >
          <BtnRadio
            text="One time payment"
            :checked="refIsOneTime"
            :disabled="refIsBusy || !refCanChooseOneTime"
            @click="setPaymentOption(DisciplinePaymentOption.OneTime)"
          />
          <BtnRadio
            text="Installment"
            :checked="refIsInstallment"
            :disabled="refIsBusy || !refCanChooseInstallment"
            @click="setPaymentOption(DisciplinePaymentOption.Installment)"
          />
        </div>
      </div>
    </div>

    <NativeSelect
      v-if="refIsOneTime"
      name="one_time_package"
      label="Package"
      placeholder="Select package"
      placeholder-enabled
      required
      :options="refPackageOptions"
      :disabled="!refPackageOptions.length"
      @change="setPackage"
    />

    <div
      v-if="refIsInstallment"
      class="discipline-payment-options__installment"
    >
      <div class="discipline-payment-options__choice">
        <span class="discipline-payment-options__label required">
          Auto renew
        </span>
        <div
          class="discipline-payment-options__radios"
          role="radiogroup"
          aria-label="Auto renew"
        >
          <BtnRadio
            text="Enable"
            :checked="refIsAutoRenew"
            @click="setAutoRenew(true)"
          />
          <BtnRadio
            text="Disable"
            :checked="!refIsAutoRenew"
            @click="setAutoRenew(false)"
          />
        </div>
      </div>
      <NativeSelect
        name="installment_package"
        label="Installments type"
        placeholder="Select installments type"
        placeholder-enabled
        required
        :options="refInstallmentOptions"
        :disabled="!refInstallmentOptions.length"
        @change="setInstallment"
      />
      <div class="discipline-payment-options__choice">
        <span class="discipline-payment-options__label required">
          Installments option
        </span>
        <div
          class="discipline-payment-options__radios"
          role="radiogroup"
          aria-label="Installments option"
        >
          <BtnRadio
            text="Every two weeks"
            disabled
          />
          <BtnRadio
            text="Monthly"
            disabled
          />
        </div>
        <AppText
          v-if="refIsFourWeeks"
          name="body2"
          color="slate-500"
        >
          This customer is billed every four weeks.
        </AppText>
      </div>
    </div>

    <div class="discipline-payment-options__list">
      <NativeSelect
        name="payment_discount"
        label="Discount"
        :options="refDiscountOptions"
        @change="setDiscount"
      />
      <DisciplineOrderExpires
        v-model="refExpiresAt"
        :timezone="refTimezone"
        :min-date="refCustomerNow"
      />
    </div>
  </div>
</template>

<style scoped lang="scss" src="./DisciplinePaymentOptions.scss" />
